import { Link } from "react-router-dom";
import Footer from "@/components/landing/Footer";
import { Wordmark } from "@/components/brand/Wordmark";

export default function Privacy() {
  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="privacy-page">
      <header className="border-b border-border">
        <div className="max-w-3xl mx-auto px-6 md:px-12 py-6 flex items-baseline justify-between">
          <Link to="/" className="font-display text-xl tracking-tight">
            <Wordmark className="h-8" />
          </Link>
          <span className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
            Last updated: 2026-02-04
          </span>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 md:px-12 py-16">
        <p className="text-[11px] uppercase tracking-[0.28em] text-oxblood font-medium mb-3">— Privacy policy</p>
        <h1 className="font-display text-4xl sm:text-5xl">Your data, our discretion.</h1>
        <p className="mt-6 text-sm text-muted-foreground leading-relaxed max-w-prose">
          This is a working draft. We'll formalise it with counsel before commercial launch.
          Placeholders are wrapped in brackets.
        </p>

        <Section title="1. Who we are">
          <p>
            Wardrobe Index is operated by [ENTITY: Wardrobe Index Ltd.] ("we", "us"). Our registered
            address is [ADDRESS]. You can reach our privacy team at <a className="text-oxblood underline underline-offset-4" href="mailto:privacy@wardrobeindex.com">privacy@wardrobeindex.com</a>.
          </p>
        </Section>

        <Section title="2. What we collect">
          <ul className="list-disc pl-5 space-y-2">
            <li><strong>Account data</strong> — your name, email, hashed password, and the workspace(s) you belong to.</li>
            <li><strong>Wardrobe data</strong> — items, photos, brands, sizes, conditions, locations, movements, and any custom fields you fill in.</li>
            <li><strong>Operational data</strong> — IP address, user agent, last sign-in timestamp. Used to secure your account.</li>
            <li><strong>Billing</strong> — when you subscribe, [PAYMENT PROCESSOR: Stripe] receives your card details. We never see them.</li>
          </ul>
        </Section>

        <Section title="3. Why we collect it">
          <ul className="list-disc pl-5 space-y-2">
            <li>To run the service you signed up for.</li>
            <li>To detect abuse, fraud, and unusual sign-ins.</li>
            <li>To send transactional emails (verify, reset, invite). We do not send marketing email by default.</li>
          </ul>
        </Section>

        <Section title="4. Who else sees it">
          <ul className="list-disc pl-5 space-y-2">
            <li>Members of your workspace, scoped by their role (owner / admin / member).</li>
            <li>Sub-processors strictly necessary for operating the service: [HOSTING], [DB], [PAYMENTS], [EMAIL]. Each is contractually bound by data-protection terms.</li>
            <li>Law enforcement only when compelled by valid legal process. We will notify you unless prohibited.</li>
          </ul>
        </Section>

        <Section title="5. How long we keep it">
          <p>
            For as long as your workspace exists. Deleting your workspace removes all items, clients,
            locations, loans, outfits, photos, and shareable links from active storage within
            [RETENTION_DAYS: 30] days. Backups are rotated within [BACKUP_DAYS: 90] days.
          </p>
        </Section>

        <Section title="6. Your rights">
          <p>
            You can export, correct, or delete your data at any time. Email <a className="text-oxblood underline underline-offset-4" href="mailto:privacy@wardrobeindex.com">privacy@wardrobeindex.com</a>.
            We respond within [SLA_DAYS: 30] calendar days.
          </p>
        </Section>

        <Section title="7. Changes">
          <p>
            We'll notify you in-app and by email at least 14 days before any material change to this
            policy.
          </p>
        </Section>

        <p className="mt-12 text-xs text-muted-foreground">[LAST UPDATED: 2026-02-04]</p>
      </main>

      <Footer />
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mt-12">
      <h2 className="font-display text-2xl">{title}</h2>
      <div className="mt-3 text-sm leading-relaxed text-foreground/80 space-y-3 max-w-prose">
        {children}
      </div>
    </section>
  );
}
